import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ReLoginModalComponent } from "@app/components/auth/auth.modal";
import { AuthTokens, AuthUser, Company } from "@app/models";
import { ModalController } from "@ionic/angular/standalone";
import {
  BehaviorSubject,
  Observable,
  of,
  Subject,
  throwError,
} from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ErrorService } from "./error.service";
import { ConnectionStatus, NetworkService } from "./network.service";
import { SKIP_AUTH_RECOVERY } from "./auth-request.context";
import { Store } from "@ngrx/store";
import { logout } from "@app/state/auth/actions/auth-api.actions";
import { PouchService } from "@app/api/pouch.service";

export class ReLoginCancelledError extends Error {
  constructor() {
    super("Re-login cancelled");
  }
}

export class InvalidRefreshResponseError extends Error {
  constructor() {
    super("Refresh response did not contain an access token");
  }
}

export class ReLoginIdentityChangedError extends Error {
  constructor() {
    super("Re-login must use the current user and company");
  }
}

interface ExpectedSessionContext {
  userId: number;
  companyId: number;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private store = inject(Store);
  private router = inject(Router);
  private errorService = inject(ErrorService);
  private networkService = inject(NetworkService);
  private modalController = inject(ModalController);
  private pouchService = inject(PouchService);

  private readonly ACCESS_TOKEN = "ACCESS_TOKEN";
  private readonly REFRESH_TOKEN = "REFRESH_TOKEN";
  private readonly LOGGED_USER = "LOGGED_USER";
  private readonly COMPANY = "COMPANY";

  private loggedIn = new BehaviorSubject<boolean>(false);
  private loginSucceeded = new Subject<void>();
  private loggedOut = new Subject<void>();

  constructor() {}

  public init() {
    const hasLocalSession = this.hasLocalSession();
    if (hasLocalSession) this.activateLocalScope();
    this.loggedIn.next(hasLocalSession);
    // Startup is offline-first. Authenticated API requests validate or recover
    // the server session after the locally persisted application has started.
    return of(true);
  }

  public ping() {
    return this.http.get(`${environment.apiUrl}/auth/valid`, {
      observe: "response",
    });
  }

  public login(user: {
    username: string;
    password: string;
  }, expectedSession?: ExpectedSessionContext): Observable<boolean> {
    return this.http
      .post<any>(`${environment.apiUrl}/auth/get_tokens`, null, {
        params: user,
      })
      .pipe(
        switchMap((auth) =>
          this.http
            .get<any>(`${environment.apiUrl}/res.users/${auth.uid}`, {
              headers: new HttpHeaders({
                "Content-Type": "text/html",
                "Access-Token": auth.access_token,
              }),
              context: new HttpContext().set(SKIP_AUTH_RECOVERY, true),
              params: {
                exclude_fields: JSON.stringify(["*"]),
                include_fields: JSON.stringify([
                  "id",
                  "login",
                  "name",
                  "email",
                  "dopa_role",
                  "company_id",
                  ["company_ids", [["id", "name"]]],
                ]),
              },
            })
            .pipe(map((loggedUser) => ({ auth, loggedUser })))
        ),
        tap(({ loggedUser }) => {
          if (!expectedSession) return;
          const companyIds = (loggedUser.company_ids || []).map(
            ({ id }) => Number(id)
          );
          if (
            Number(loggedUser.id) !== expectedSession.userId ||
            !companyIds.includes(expectedSession.companyId)
          ) {
            throw new ReLoginIdentityChangedError();
          }
        }),
        tap(({ auth }) =>
          this.storeTokens({
            accessToken: auth.access_token,
            refreshToken: auth.refresh_token,
          })
        ),
        tap(({ loggedUser }) => this.storeLoggedUser(loggedUser)),
        tap(() => this.activateLocalScope()),
        tap(() => this.loggedIn.next(true)),
        tap(() => this.loginSucceeded.next()),
        map(() => true),
        catchError((error) => this.errorService.handleError(error))
      );
  }

  public logout() {
    this.loggedOut.next();
    this.pouchService.deactivateScope();
    this.removeTokens();
    this.loggedIn.next(false);
    this.store.dispatch(logout());
    this.router.navigate(["/", "auth"]);
  }

  public showReLoginModal(username: string = ""): Observable<{
    username: string;
    password: string;
  } | null> {
    return new Observable((subscriber) => {
      let modal: HTMLIonModalElement | null = null;
      const offlineSubscription = this.networkService
        .onNetworkChange()
        .pipe(
          filter((status) => status === ConnectionStatus.Offline),
          take(1)
        )
        .subscribe(() => {
          modal?.dismiss();
          if (!subscriber.closed) {
            subscriber.next(null);
            subscriber.complete();
          }
        });

      this.modalController
        .create({
          component: ReLoginModalComponent,
          componentProps: { username },
        })
        .then(async (createdModal) => {
          modal = createdModal;
          if (subscriber.closed) {
            await modal.dismiss();
            return;
          }
          await modal.present();
          const { data, role } = await modal.onWillDismiss();
          if (!subscriber.closed && role === "switch-account") {
            this.logout();
            subscriber.complete();
            return;
          }
          if (!subscriber.closed) {
            subscriber.next(data);
            subscriber.complete();
          }
        })
        .catch((error) => subscriber.error(error));

      return () => {
        offlineSubscription.unsubscribe();
        modal?.dismiss();
      };
    });
  }

  public promptReLogin(): Observable<string> {
    const loggedUser = this.getLoggedUser();
    const expectedSession = {
      userId: Number(loggedUser.id),
      companyId: Number(this.getCompany()),
    };
    return this.showReLoginModal(loggedUser.login).pipe(
      switchMap((credentials) => {
        if (credentials) {
          return this.login(credentials, expectedSession).pipe(
            map(() => this.getAccessToken()),
            catchError((error) =>
              error === "User authentication failed!"
                ? this.promptReLogin()
                : throwError(() => error)
            )
          );
        } else {
          return throwError(() => new ReLoginCancelledError());
        }
      })
    );
  }

  public isLoggedIn() {
    return this.loggedIn.getValue();
  }

  public onLoginSuccess(): Observable<void> {
    return this.loginSucceeded.asObservable();
  }

  public onLogout(): Observable<void> {
    return this.loggedOut.asObservable();
  }

  public refreshToken() {
    return this.http
      .post<any>(`${environment.apiUrl}/auth/refresh_token`, {
        refresh_token: this.getRefreshToken(),
      }, {
        // The Odoo endpoint is an HTTP route. Sending Angular's default
        // application/json content type makes Odoo dispatch it as a JSON-RPC
        // request and reject the route before the controller runs.
        headers: new HttpHeaders({ "Content-Type": "text/html" }),
      })
      .pipe(
        tap((response: any) => {
          if (
            typeof response?.access_token !== "string" ||
            response.access_token.length === 0
          ) {
            throw new InvalidRefreshResponseError();
          }
          this.storeAccessToken(response.access_token);
        })
      );
  }

  public getAccessToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  public getLoggedUser(): AuthUser {
    const loggedUser = localStorage.getItem(this.LOGGED_USER);
    if (!loggedUser) {
      return new AuthUser();
    }
    try {
      return new AuthUser(JSON.parse(loggedUser));
    } catch (error) {
      return new AuthUser();
    }
  }

  private getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  private hasLocalSession(): boolean {
    return Boolean(
      this.getAccessToken() &&
        this.getRefreshToken() &&
        this.getLoggedUser().id
    );
  }

  private storeAccessToken(accessToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN, accessToken);
  }

  private storeTokens(tokens: AuthTokens): void {
    localStorage.setItem(this.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN, tokens.refreshToken);
  }

  private storeLoggedUser(user: AuthUser): void {
    localStorage.setItem(this.LOGGED_USER, JSON.stringify(user));
    const companyId = this.getCompany();
    if (
      !companyId ||
      !user.company_ids.map(({ id }) => id).includes(companyId)
    ) {
      const newCompanyId = user.company_id.toString();
      this.storeCompany(newCompanyId);
    }
  }

  private removeTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
  }

  public getCompanies(): Company[] {
    const user = this.getLoggedUser();
    return user.company_ids.map((company) => new Company(company));
  }

  public getCompany(): number {
    const company = localStorage.getItem(this.COMPANY);
    return company ? parseInt(company) : null;
  }

  public storeCompany(companyId: string): void {
    localStorage.setItem(this.COMPANY, companyId);
  }

  public activateLocalScope(): void {
    const userId = this.getLoggedUser().id;
    let companyId = this.getCompany();
    if (!companyId && this.getLoggedUser().company_id) {
      companyId = Number(this.getLoggedUser().company_id);
      this.storeCompany(companyId.toString());
    }
    if (!userId || !companyId) {
      throw new Error("Cannot activate local storage without user and company");
    }
    this.pouchService.activateScope(userId, companyId);
  }
}
